import React, { useMemo, useState } from 'react'

import { useSelector } from 'react-redux'

import { useSnackbar } from '../../../../../../../../../../shared/components/Snackbar/context/SnackBar.context.container'

import Button from '../../../../../../../../../../shared/components/Button/Button.component'
import Modal from '../../../../../../../../../../shared/components/Modal/Modal.component'

import { releaseOfferRequestForAdminThunk } from '../../../../../../../../../../redux/states/admin/orders/releaseOfferRequestForAdmin/releaseOfferRequestForAdmin.slice'
import { RootState } from '../../../../../../../../../../redux/store/store.types'
import status from '../../../../../../../../../../shared/functions/Status/status.dictionaries'
import { useAppDispatch } from '../../../../../../../../../../shared/hooks/redux.hooks'
import styles from './ReleaseOfferRequestButton.styles'
import { ReleaseOfferRequestButtonProps } from './ReleaseOfferRequestButton.types'

const ReleaseOfferRequestButton: React.FC<ReleaseOfferRequestButtonProps> = (props) => {
  const { offerRequestId, onSuccess } = props

  const [isModalOpen, setIsModalOpen] = useState(false)
  const dispatch = useAppDispatch()
  const { show } = useSnackbar()

  const { loading: releaseLoading, success: releaseOfferRequestSuccess } = useSelector(
    (state: RootState) => state.releaseOfferRequestForAdmin
  )
  const { success: offerRequestsSuccess } = useSelector((state: RootState) => state.getAllOfferRequestsForAdmin)

  const offerRequest = offerRequestsSuccess?.data?.records.find((offerRequest) => offerRequest.id === offerRequestId)

  const isOfferRequestReleased = useMemo(() => {
    // If we have a successful release for this specific offer request
    if (releaseOfferRequestSuccess) {
      return true // Released successfully
    }

    // Check if the offer request is already in a released state (pending acceptance by traveler)
    return offerRequest?.status?.name === status.offerRequestStatus.pending
  }, [offerRequest?.status?.name, releaseOfferRequestSuccess])

  const isOfferRequestCancelled = useMemo(() => {
    return (
      offerRequest?.status?.name === status.itemRequestStatus.cancelledByShopper ||
      offerRequest?.status?.name === status.offerRequestStatus.cancelled_by_admin
    )
  }, [offerRequest?.status?.name])

  const getButtonText = () => {
    if (isOfferRequestReleased) {
      return 'Offer Released'
    }

    return 'Release Offer'
  }

  const getTooltipText = () => {
    if (isOfferRequestCancelled) {
      return 'Cannot release cancelled offer request'
    }

    if (isOfferRequestReleased) {
      return 'Offer already released to marketplace'
    }

    return 'Release offer request back to marketplace'
  }

  const isButtonDisabled = isOfferRequestCancelled || isOfferRequestReleased

  const handleReleaseClick = () => {
    if (!isButtonDisabled) {
      setIsModalOpen(true)
    }
  }

  const handleConfirmRelease = () => {
    dispatch(
      releaseOfferRequestForAdminThunk({
        offerRequestId: String(offerRequestId)
      })
    )
      .unwrap()
      .then(() => {
        setIsModalOpen(false)
        show({ message: 'Offer request released successfully.', severity: 'success' })
        onSuccess && onSuccess()
      })
      .catch(() => {
        show({ message: 'Failed to release offer request.', severity: 'error' })
      })
  }

  const handleCloseModal = () => {
    setIsModalOpen(false)
  }

  return (
    <>
      <Button
        text={getButtonText()}
        buttonType="secondary"
        disabled={isButtonDisabled}
        onClick={handleReleaseClick}
        tooltipText={getTooltipText()}
        style={styles.button}
      />

      <Modal
        open={isModalOpen}
        onClose={handleCloseModal}
        title="Release Offer Request"
        primaryButton={{
          label: 'Confirm',
          onClick: handleConfirmRelease,
          loading: releaseLoading
        }}
        secondaryButton={{
          label: 'Cancel',
          onClick: handleCloseModal
        }}
        body="Are you sure you want to release this offer request back to the marketplace? This will remove the traveler assignment and make it available for other travelers to accept."
      />
    </>
  )
}

export default ReleaseOfferRequestButton
